import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Banknote,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  History,
  Home,
  Loader2,
  MapPin,
  Minus,
  Pause,
  Phone,
  Plus,
  Receipt,
  Search,
  Send,
  ShoppingBag,
  Trash2,
  Truck,
  User,
  Utensils,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleProvider";
import { useCurrency } from "@/hooks/useCurrency";
import { useCounterShift } from "@/hooks/useCounterShift";
import { canAccessDashboard } from "@/lib/permissions";
import api from "@/lib/api";
import { aed } from "@/lib/format";
import { cn } from "@/lib/utils";
import { itemHasSizes } from "@/lib/menuItemSizes";
import { displayItemName } from "@/lib/menuLocale";
import { resolveMediaUrl } from "@/lib/mediaUrl";
import { cancelPreparedBillPrint, prepareBillPrintWindow, printBillReceipt } from "@/lib/billPrint";
import { dispatchKotJobsLan } from "@/lib/kotPrint";
import { runPosBootstrap } from "@/lib/posBootstrap";
import DayOpenGate from "@/components/pos/DayOpenGate";
import PosSizePickerPopup from "@/components/pos/PosSizePickerPopup";
import PosProductPlaceholder from "@/components/pos/PosProductPlaceholder";
import SimpleBillHistorySheet from "@/components/simple/SimpleBillHistorySheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import "@/styles/simple-ui.css";

const ORDER_TYPES = [
  { id: "DI", labelKey: "pos.dineIn", icon: Utensils, bg: "bg-[#F97316]" },
  { id: "TA", labelKey: "pos.takeAway", icon: ShoppingBag, bg: "bg-[#2563EB]" },
  { id: "DL", labelKey: "pos.delivery", icon: Truck, bg: "bg-[#059669]" },
];

function notifyKotJobs(data) {
  const jobs = data?.kot_jobs || [];
  if (!jobs.length) return;
  dispatchKotJobsLan(jobs);
  toast.success("KOT sent to kitchen");
}

export default function SimplePOS() {
  const { t } = useTranslation();
  const { isRtl } = useLocale();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, refreshUser } = useAuth();
  const { formatPlain, code: currencyCode } = useCurrency();

  const branchId = user?.default_branch?.id;
  const counterId = user?.default_counter?.id;
  const counterName = user?.default_counter?.name || "Counter";
  const showBackOffice = canAccessDashboard(user?.permissions || []);

  const { data: shiftData, isLoading: shiftLoading, refetch: refetchShift } = useCounterShift(branchId, counterId);
  const shiftOpen = Boolean(shiftData?.session);

  const { data: businessDayPayload, isLoading: businessDayLoading, isFetching: businessDayFetching } = useQuery({
    queryKey: ["business-day", branchId],
    queryFn: async () => {
      const { data } = await api.get("/business-day/current", { params: { branch_id: branchId } });
      return data;
    },
    enabled: Boolean(branchId),
    staleTime: 0,
    refetchOnMount: "always",
  });
  const businessDay = businessDayPayload?.business_day;
  const dayIsOpen = businessDay?.status === "open";

  const openDayMutation = useMutation({
    mutationFn: async ({ businessDate, twoFactorCode }) => {
      const payload = { branch_id: branchId, business_date: businessDate };
      if (twoFactorCode) payload.two_factor_code = twoFactorCode;
      const { data } = await api.post("/business-day/open", payload);
      return data;
    },
    onSuccess: async (data) => {
      if (data?.business_day) {
        queryClient.setQueryData(["business-day", branchId], (prev) => ({
          ...(prev && typeof prev === "object" ? prev : {}),
          business_day: data.business_day,
        }));
      }
      await queryClient.invalidateQueries({ queryKey: ["business-day", branchId] });
      toast.success(data?.message || "Business day opened");
    },
  });

  const [shiftFloat, setShiftFloat] = useState("0");
  const openShiftMutation = useMutation({
    mutationFn: async () => {
      const { data: res } = await api.post("/counter-sessions/open", {
        branch_id: branchId,
        counter_id: counterId,
        opening_float: Number(shiftFloat) || 0,
      });
      return res;
    },
    onSuccess: async (res) => {
      toast.success(res.message || "Shift opened.");
      await refreshUser();
      await refetchShift();
    },
    onError: (err) => toast.error(err.response?.data?.message || "Could not open shift."),
  });

  const [bill, setBill] = useState(null);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [category, setCategory] = useState("ALL");
  const [search, setSearch] = useState("");
  const [sizePickItem, setSizePickItem] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [payMethod, setPayMethod] = useState("cash");
  const [tendered, setTendered] = useState("");
  const [heldOpen, setHeldOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [discountOpen, setDiscountOpen] = useState(false);
  const [discountPct, setDiscountPct] = useState("");

  const { data: menuItems = [], isLoading: menuLoading } = useQuery({
    queryKey: ["pos-menu-items", branchId],
    queryFn: async () => {
      const { data } = await api.get("/menu-items", {
        params: { branch_id: branchId, show_in_pos: 1 },
      });
      return (data.menu_items || []).filter((m) => m.show_in_pos !== false && m.status === "Active");
    },
    enabled: Boolean(branchId && shiftOpen && dayIsOpen),
  });

  const categories = useMemo(() => {
    const set = new Set(menuItems.map((m) => m.category || "Other"));
    return ["ALL", ...Array.from(set)];
  }, [menuItems]);

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    return menuItems.filter((m) => {
      if (category !== "ALL" && (m.category || "Other") !== category) return false;
      if (!q) return true;
      const name = displayItemName(m).toLowerCase();
      return name.includes(q) || String(m.code || "").toLowerCase().includes(q);
    });
  }, [menuItems, category, search]);

  const { data: heldBillsData, refetch: refetchHeld } = useQuery({
    queryKey: ["held-bills", branchId],
    queryFn: async () => {
      const { data: res } = await api.get("/held-bills", { params: { branch_id: branchId } });
      return res;
    },
    enabled: Boolean(branchId && shiftOpen && dayIsOpen),
    refetchInterval: 15000,
  });
  const heldBills = heldBillsData?.held_bills ?? [];

  const { data: sessionNav } = useQuery({
    queryKey: ["pos-session-nav", branchId, counterId, bill?.id],
    queryFn: async () => {
      const { data } = await api.get("/bills/session-nav", {
        params: { branch_id: branchId, counter_id: counterId, current_id: bill.id },
      });
      return data;
    },
    enabled: Boolean(branchId && counterId && bill?.id && shiftOpen && dayIsOpen),
  });

  const sessionNavMutation = useMutation({
    mutationFn: async (direction) => {
      if (!bill?.id) throw new Error("No bill loaded");
      const { data } = await api.get("/bills/session-nav", {
        params: {
          branch_id: branchId,
          counter_id: counterId,
          current_id: bill.id,
          direction,
        },
      });
      return data;
    },
    onSuccess: (data) => {
      const paid = data.bill.status === "paid" || data.bill.status === "partial_return";
      const openBiz = businessDay?.business_date;
      const billBiz = data.bill.business_date || String(data.bill.paid_at || data.bill.created_at || "").slice(0, 10);
      const otherDay = Boolean(openBiz && billBiz && billBiz !== openBiz);
      setBill(data.bill);
      queryClient.setQueryData(["pos-session-nav", branchId, counterId, data.bill.id], {
        has_prev: data.has_prev,
        has_next: data.has_next,
      });
      toast.success(
        data.recalled
          ? `${data.bill.bill_number} recalled`
          : paid
            ? `${data.bill.bill_number} · paid (view only)`
            : otherDay
              ? `${data.bill.bill_number} · ${billBiz} (view only)`
              : `${data.bill.bill_number} loaded`,
      );
    },
    onError: (err) => toast.error(err.response?.data?.message || err.message || "No more bills"),
  });

  const reprintMutation = useMutation({
    mutationFn: async (billId) => {
      const { data } = await api.post(`/bills/${billId}/reprint`, {}, { params: { branch_id: branchId } });
      return data;
    },
    onSuccess: (data) => {
      if (data.print_receipt) {
        printBillReceipt(data.print_receipt);
        toast.success(data.print_receipt.dispatch_message || `Reprint ${data.bill?.bill_number || ""}`);
        setHistoryOpen(false);
      } else {
        cancelPreparedBillPrint();
        toast.error("Reprint ready but no receipt HTML");
      }
    },
    onError: (err) => {
      cancelPreparedBillPrint();
      toast.error(err.response?.data?.message || "Reprint failed");
    },
  });

  const openHistoryBillMutation = useMutation({
    mutationFn: async (row) => {
      const { data } = await api.get(`/bills/${row.id}`, { params: { branch_id: branchId } });
      return data.bill;
    },
    onSuccess: (loaded) => {
      setBill(loaded);
      setHistoryOpen(false);
      const openBiz = businessDay?.business_date;
      const billBiz = loaded.business_date || String(loaded.paid_at || "").slice(0, 10);
      const editable = (loaded.status === "open" || loaded.status === "held") && openBiz && billBiz === openBiz;
      toast.success(
        editable
          ? `${loaded.bill_number} opened for edit`
          : `${loaded.bill_number} · view only (same POS day)`,
      );
    },
    onError: (err) => toast.error(err.response?.data?.message || "Could not open bill"),
  });

  useEffect(() => {
    if (!branchId || !counterId || !shiftOpen || !dayIsOpen) {
      setBootstrapping(false);
      return undefined;
    }
    let cancelled = false;
    setBootstrapping(true);
    const state = location.state || {};
    const bootKey = [
      "simple-pos",
      branchId,
      counterId,
      location.key,
      state.openBillId ?? "",
      state.openTable?.restaurant_table_id ?? "",
      state.recallIfHeld ? "1" : "",
    ].join(":");

    runPosBootstrap(bootKey, async () => {
      if (state.recalledBill) {
        return { bill: state.recalledBill };
      }
      if (state.openBillId) {
        if (state.recallIfHeld) {
          const { data: res } = await api.post(
            `/bills/${state.openBillId}/recall`,
            {},
            { params: { branch_id: branchId } },
          );
          return { bill: res.bill, toastMessage: `Opened ${state.tableCode || res.bill.bill_number}` };
        }
        const { data: res } = await api.get(`/bills/${state.openBillId}`, {
          params: { branch_id: branchId },
        });
        return { bill: res.bill, toastMessage: `Opened ${state.tableCode || res.bill.bill_number}` };
      }
      if (state.openTable) {
        const { data } = await api.post(
          "/bills",
          {
            order_type: "DI",
            table_code: state.openTable.table_code,
            restaurant_table_id: state.openTable.restaurant_table_id,
            customer_name: "Walk-in",
          },
          { params: { branch_id: branchId, counter_id: counterId } },
        );
        return {
          bill: data.bill,
          toastMessage: `Table ${state.openTable.table_name || state.openTable.table_code}`,
        };
      }
      // Straight into billing — order type switches live on the top bar (TA default).
      const { data } = await api.post(
        "/bills",
        { order_type: "TA", customer_name: "Walk-in" },
        { params: { branch_id: branchId, counter_id: counterId } },
      );
      return { bill: data.bill };
    })
      .then((result) => {
        if (cancelled || !result?.bill) return;
        setBill(result.bill);
        if (result.toastMessage) toast.success(result.toastMessage);
        if (state.openBillId || state.openTable || state.recalledBill) {
          navigate("/pos", { replace: true, state: {} });
        }
      })
      .catch((err) => {
        if (!cancelled) toast.error(err.response?.data?.message || "Failed to open POS");
      })
      .finally(() => {
        if (!cancelled) setBootstrapping(false);
      });
    return () => {
      cancelled = true;
    };
  }, [branchId, counterId, shiftOpen, dayIsOpen, location.key, location.state, navigate]);

  const createBillMutation = useMutation({
    mutationFn: async (payload) => {
      const { data } = await api.post("/bills", payload, {
        params: { branch_id: branchId, counter_id: counterId },
      });
      return data.bill;
    },
    onSuccess: (newBill) => {
      setBill(newBill);
      toast.success(`${newBill.bill_number}`);
    },
    onError: () => toast.error("Failed to create bill"),
  });

  const updateOrderType = useMutation({
    mutationFn: async (orderType) => {
      if (!bill?.id) {
        const { data } = await api.post(
          "/bills",
          { order_type: orderType, customer_name: "Walk-in" },
          { params: { branch_id: branchId, counter_id: counterId } },
        );
        return data.bill;
      }
      const { data } = await api.put(
        `/bills/${bill.id}`,
        { order_type: orderType },
        { params: { branch_id: branchId } },
      );
      return data.bill;
    },
    onSuccess: (updated) => setBill(updated),
    onError: (err) => toast.error(err.response?.data?.message || "Could not set order type"),
  });

  const lineMutation = useMutation({
    mutationFn: async ({ action, lineId, payload }) => {
      if (action === "add") {
        const { data } = await api.post(`/bills/${bill.id}/lines`, payload, {
          params: { branch_id: branchId },
        });
        return data;
      }
      if (action === "void") {
        const { data } = await api.post(`/bills/${bill.id}/lines/${lineId}/void`, {}, {
          params: { branch_id: branchId },
        });
        return data;
      }
      const { data } = await api.patch(`/bills/${bill.id}/lines/${lineId}`, payload, {
        params: { branch_id: branchId },
      });
      return data;
    },
    onSuccess: (data) => {
      setBill(data.bill);
      notifyKotJobs(data);
    },
    onError: (err) => toast.error(err.response?.data?.message || "Failed to update line"),
  });

  const holdMutation = useMutation({
    mutationFn: async () => {
      const { data } = await api.post(`/bills/${bill.id}/hold`, {}, { params: { branch_id: branchId } });
      return data;
    },
    onSuccess: async () => {
      toast.success("Bill held");
      refetchHeld();
      const { data } = await api.post(
        "/bills",
        { order_type: bill?.order_type || "TA", customer_name: "Walk-in" },
        { params: { branch_id: branchId, counter_id: counterId } },
      );
      setBill(data.bill);
    },
    onError: (err) => toast.error(err.response?.data?.message || "Hold failed"),
  });

  const recallMutation = useMutation({
    mutationFn: async (heldId) => {
      if (bill?.id && (bill.lines?.length || 0) > 0 && bill.status === "open") {
        await api.post(`/bills/${bill.id}/hold`, {}, { params: { branch_id: branchId } });
      }
      const { data } = await api.post(`/bills/${heldId}/recall`, {}, { params: { branch_id: branchId } });
      return data.bill;
    },
    onSuccess: (recalled) => {
      setBill(recalled);
      setHeldOpen(false);
      refetchHeld();
      toast.success(`Recalled ${recalled.bill_number}`);
    },
    onError: (err) => toast.error(err.response?.data?.message || "Recall failed"),
  });

  const sendKotMutation = useMutation({
    mutationFn: async () => {
      const { data } = await api.post(`/bills/${bill.id}/kot/send`, {}, { params: { branch_id: branchId } });
      return data;
    },
    onSuccess: (data) => {
      if (!(data?.kot_jobs || []).length) toast.info("No kitchen tickets to send");
      else notifyKotJobs(data);
    },
    onError: (err) => toast.error(err.response?.data?.message || "KOT failed"),
  });

  const discountMutation = useMutation({
    mutationFn: async (percent) => {
      const { data } = await api.post(
        `/bills/${bill.id}/discount`,
        { percent: Number(percent) },
        { params: { branch_id: branchId } },
      );
      return data.bill;
    },
    onSuccess: (updated) => {
      setBill(updated);
      setDiscountOpen(false);
      setDiscountPct("");
      toast.success("Discount applied");
    },
    onError: (err) => toast.error(err.response?.data?.message || "Discount failed"),
  });

  const paymentMutation = useMutation({
    mutationFn: async ({ method, tenderedAmount, deliveryPayload }) => {
      let current = bill;
      const previousNet = Number(bill?.net_amount || 0);
      if (deliveryPayload) {
        const { data } = await api.put(`/bills/${bill.id}`, deliveryPayload, {
          params: { branch_id: branchId },
        });
        current = data.bill;
        setBill(current);
      }
      const customerName = deliveryPayload?.customer_name || current?.customer_name || "Walk-in";
      const due = Number(current?.net_amount || 0);
      let cashTendered = Number(tenderedAmount) || due;
      // If cashier left the prefilled total, follow bill total after delivery charge applies.
      if (method === "cash" && deliveryPayload && Math.abs(cashTendered - previousNet) < 0.02) {
        cashTendered = due;
      }
      const base = `/bills/${current.id}/payments`;
      if (method === "cash") {
        const { data } = await api.post(
          `${base}/cash`,
          { tendered_amount: cashTendered, customer_name: customerName },
          { params: { branch_id: branchId } },
        );
        return data;
      }
      const { data } = await api.post(
        `${base}/card`,
        { customer_name: customerName },
        { params: { branch_id: branchId } },
      );
      return data;
    },
    onSuccess: (data) => {
      setPayOpen(false);
      setTendered("");
      const change = Number(data.change_amount || 0);
      let printed = false;
      if (data.print_receipt) {
        // One browser print dialog only (prepared on Pay click).
        printed = printBillReceipt(data.print_receipt);
      } else {
        cancelPreparedBillPrint();
      }
      const printNote = printed || data.print_receipt ? " · receipt" : "";
      toast.success(
        change > 0
          ? `Paid — change ${formatPlain(change)} ${currencyCode}${printNote}`
          : `${data.message || "Paid"}${printNote}`,
      );
      queryClient.invalidateQueries({ queryKey: ["pos-session-nav", branchId, counterId] });
      if (data.new_bill?.id) {
        setBill(data.new_bill);
      } else {
        createBillMutation.mutate({ order_type: bill?.order_type || "TA", customer_name: "Walk-in" });
      }
    },
    onError: (err) => {
      cancelPreparedBillPrint();
      const errors = err.response?.data?.errors;
      const msg =
        err.response?.data?.message
        || errors?.delivery_contact?.[0]
        || errors?.delivery_address?.[0]
        || errors?.delivery_area_id?.[0]
        || (errors ? Object.values(errors).flat()[0] : null)
        || "Payment failed";
      toast.error(msg);
    },
  });

  const addItem = useCallback((item, size = null) => {
    if (!bill?.id || lineMutation.isPending) return;
    if (!size && itemHasSizes(item)) {
      setSizePickItem(item);
      return;
    }
    lineMutation.mutate({
      action: "add",
      payload: {
        menu_item_id: item.id,
        qty: 1,
        size: size || undefined,
      },
    });
  }, [bill?.id, lineMutation]);

  const lines = bill?.lines ?? [];
  const net = Number(bill?.net_amount ?? 0);
  const busy = lineMutation.isPending || paymentMutation.isPending || holdMutation.isPending;
  const orderType = bill?.order_type || "TA";
  const openBusinessDate = businessDay?.business_date || null;
  const billBusinessDate = bill?.business_date
    ? String(bill.business_date).slice(0, 10)
    : null;
  const billOnOpenDay = Boolean(
    openBusinessDate
    && (
      !billBusinessDate
        ? (bill?.status === "open" || bill?.status === "held")
        : billBusinessDate === openBusinessDate
    ),
  );
  const billEditable = (bill?.status === "open" || bill?.status === "held") && billOnOpenDay;
  const billViewOnly = Boolean(bill) && !billEditable;
  const canGoNext = Boolean(sessionNav?.has_next) || (Boolean(bill) && !(billEditable && lines.length === 0));

  const goSessionPrev = () => {
    if (!sessionNav?.has_prev || sessionNavMutation.isPending) return;
    sessionNavMutation.mutate("prev");
  };
  const goSessionNext = () => {
    if (sessionNavMutation.isPending || createBillMutation.isPending) return;
    if (sessionNav?.has_next) {
      sessionNavMutation.mutate("next");
      return;
    }
    if (billEditable && lines.length === 0) return;
    createBillMutation.mutate({ order_type: orderType || "TA", customer_name: "Walk-in" });
  };

  const guardEdit = () => {
    if (billEditable) return true;
    toast.info(
      openBusinessDate
        ? t("simple.posViewOnlyDay", "Only invoices for POS day {{date}} can be edited", { date: openBusinessDate })
        : t("simple.posViewOnly", "This invoice is view only"),
    );
    return false;
  };

  if (!branchId || !counterId) {
    return (
      <div className="grid h-full place-items-center p-6 text-center text-slate-600">
        <p>{t("pos.selectBranchCounter", "Select branch and counter first")}</p>
        <Link to="/" className="mt-3 text-violet-700 font-semibold">{t("common.home")}</Link>
      </div>
    );
  }

  if (businessDayLoading) {
    return (
      <div className="grid h-full place-items-center gap-2 text-slate-600">
        <Loader2 className="h-6 w-6 animate-spin" />
        Checking business day…
      </div>
    );
  }

  if (!dayIsOpen) {
    return (
      <DayOpenGate
        businessDay={businessDay}
        busy={openDayMutation.isPending || businessDayFetching}
        onOpenDay={async (businessDate, twoFactorCode) => {
          await openDayMutation.mutateAsync({ businessDate, twoFactorCode });
        }}
        onLeave={() => navigate(showBackOffice ? "/" : "/login")}
      />
    );
  }

  if (shiftLoading || bootstrapping) {
    return (
      <div className="grid h-full place-items-center gap-2 text-slate-600">
        <Loader2 className="h-6 w-6 animate-spin" />
        {t("common.loading")}
      </div>
    );
  }

  if (!shiftOpen) {
    return (
      <div className="simple-ui grid h-full place-items-center p-4" dir={isRtl ? "rtl" : "ltr"}>
        <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-xl">
          <h2 className="text-xl font-extrabold text-slate-900">{t("pos.shiftRequiredTitle", "Open shift")}</h2>
          <p className="mt-2 text-sm text-slate-600">
            {t("pos.shiftRequiredBody", { counter: counterName })}
          </p>
          <div className="mt-4 space-y-1.5">
            <Label>{t("counter.openingFloat", "Opening float")}</Label>
            <Input value={shiftFloat} onChange={(e) => setShiftFloat(e.target.value)} type="number" min="0" className="h-12" />
          </div>
          <Button
            className="mt-5 h-12 w-full bg-violet-600 text-base font-bold hover:bg-violet-700"
            disabled={openShiftMutation.isPending}
            onClick={() => openShiftMutation.mutate()}
          >
            {openShiftMutation.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
            {t("pos.openShift", "Open shift")}
          </Button>
          <button type="button" className="mt-3 w-full text-sm text-slate-500" onClick={() => navigate("/")}>
            {t("common.home")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="simple-ui flex h-full min-h-0 flex-col bg-[#EEF0F7]" dir={isRtl ? "rtl" : "ltr"} data-testid="simple-pos">
      {/* Row 1: home · prev · invoice # · next · history · held */}
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-white px-3 py-2">
        <Link to="/" className="inline-flex h-11 items-center gap-1.5 rounded-xl bg-violet-100 px-3 text-sm font-bold text-violet-800">
          <Home className="h-4 w-4" />
          <span className="hidden sm:inline">{t("common.home")}</span>
        </Link>
        <div className="flex items-center gap-1">
          <button
            type="button"
            data-testid="simple-pos-bill-prev"
            title={t("simple.posPrevBill", "Previous invoice")}
            disabled={!sessionNav?.has_prev || sessionNavMutation.isPending}
            onClick={goSessionPrev}
            className="grid h-11 w-11 place-items-center rounded-xl border border-slate-200 bg-slate-50 text-slate-700 disabled:opacity-40"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div
            className="inline-flex h-11 min-w-[140px] max-w-[200px] items-center justify-center gap-1.5 rounded-xl bg-slate-900 px-3 text-white"
            data-testid="simple-pos-bill-number"
          >
            <Receipt className="h-4 w-4 shrink-0 text-violet-300" />
            <span className="truncate font-mono text-sm font-extrabold">{bill?.bill_number || "—"}</span>
          </div>
          <button
            type="button"
            data-testid="simple-pos-bill-next"
            title={t("simple.posNextBill", "Next invoice")}
            disabled={!canGoNext || sessionNavMutation.isPending}
            onClick={goSessionNext}
            className="grid h-11 w-11 place-items-center rounded-xl border border-slate-200 bg-slate-50 text-slate-700 disabled:opacity-40"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
        {billViewOnly ? (
          <span className="rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-bold uppercase text-amber-900">
            {t("simple.posViewOnly", "View only")}
          </span>
        ) : null}
        <div className="ms-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => setHistoryOpen(true)}
            className="inline-flex h-11 items-center gap-1.5 rounded-xl bg-slate-100 px-3 text-sm font-bold text-slate-800"
            data-testid="simple-pos-history-btn"
          >
            <History className="h-4 w-4" />
            <span className="hidden sm:inline">{t("simple.posHistory", "History")}</span>
          </button>
          <button type="button" onClick={() => setHeldOpen(true)} className="h-11 rounded-xl bg-amber-100 px-3 text-sm font-bold text-amber-900">
            {t("simple.posHeld", "Held")} ({heldBills.length})
          </button>
        </div>
      </div>

      {/* Row 2: order types */}
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-white px-3 py-2">
        <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto">
          {ORDER_TYPES.map((ot) => {
            const Icon = ot.icon;
            const active = orderType === ot.id;
            return (
              <button
                key={ot.id}
                type="button"
                disabled={!billEditable || updateOrderType.isPending || busy}
                onClick={() => {
                  if (!guardEdit()) return;
                  if (orderType === ot.id) return;
                  updateOrderType.mutate(ot.id);
                }}
                className={cn(
                  "inline-flex h-11 shrink-0 items-center gap-1.5 rounded-xl px-3 text-sm font-extrabold text-white transition",
                  ot.bg,
                  active ? "ring-2 ring-offset-2 ring-slate-900/40 scale-[1.02]" : "opacity-70 hover:opacity-100",
                  !billEditable && "opacity-50",
                )}
                data-testid={`simple-pos-type-${ot.id}`}
              >
                <Icon className="h-4 w-4" strokeWidth={2.5} />
                {t(ot.labelKey)}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        {/* Products */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <div className="space-y-2 border-b border-slate-200 bg-white p-3">
            <div className="relative">
              <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("common.search")}
                className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 ps-9 pe-3 text-sm outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100"
              />
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {categories.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCategory(c)}
                  className={cn(
                    "shrink-0 rounded-full px-3 py-1.5 text-xs font-bold",
                    category === c ? "bg-violet-600 text-white" : "bg-slate-100 text-slate-600",
                  )}
                >
                  {c === "ALL" ? t("simple.posAll", "All") : c}
                </button>
              ))}
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto p-3">
            {menuLoading ? (
              <div className="grid place-items-center py-16 text-slate-500">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-4">
                {filteredItems.map((item) => (
                  <ProductTile key={item.id} item={item} disabled={busy || !billEditable} onAdd={() => { if (!guardEdit()) return; addItem(item); }} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Cart */}
        <aside className="flex max-h-[45vh] min-h-[220px] w-full flex-col border-t border-slate-200 bg-white lg:max-h-none lg:w-[360px] lg:border-s lg:border-t-0">
          <div className="border-b px-3 py-2 text-sm font-extrabold text-slate-800">
            {t("simple.posCart", "Current bill")} · {lines.length}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-2">
            {lines.length === 0 ? (
              <div className="grid h-full place-items-center p-6 text-center text-sm text-slate-400">
                {t("simple.posEmptyCart", "Tap a product to add")}
              </div>
            ) : (
              <ul className="space-y-2">
                {lines.map((line) => (
                  <li key={line.id} className="flex items-center gap-2 rounded-xl border border-slate-100 bg-slate-50 p-2">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-bold text-slate-800">{line.name || line.item_name}</div>
                      <div className="font-mono text-xs text-slate-500">{aed(line.line_total ?? line.amount)}</div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        className="grid h-9 w-9 place-items-center rounded-lg bg-white border"
                        disabled={busy || !billEditable}
                        onClick={() => {
                          if (!guardEdit()) return;
                          const qty = Number(line.qty) || 1;
                          if (qty <= 1) lineMutation.mutate({ action: "void", lineId: line.id });
                          else lineMutation.mutate({ action: "patch", lineId: line.id, payload: { qty: qty - 1 } });
                        }}
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                      <span className="w-7 text-center text-sm font-extrabold">{line.qty}</span>
                      <button
                        type="button"
                        className="grid h-9 w-9 place-items-center rounded-lg bg-white border"
                        disabled={busy || !billEditable}
                        onClick={() => {
                          if (!guardEdit()) return;
                          lineMutation.mutate({ action: "patch", lineId: line.id, payload: { qty: (Number(line.qty) || 1) + 1 } });
                        }}
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="space-y-2 border-t p-3">
            <div className="flex items-end justify-between">
              <span className="text-sm font-semibold text-slate-500">{t("simple.posTotal", "Total")}</span>
              <span className="text-2xl font-black tabular-nums text-violet-700">{aed(net)}</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <ActionBtn color="bg-sky-500" icon={Send} label={t("pos.action.sendKot", "KOT")} disabled={!billEditable || busy || !lines.length} onClick={() => { if (!guardEdit()) return; sendKotMutation.mutate(); }} />
              <ActionBtn color="bg-amber-500" icon={Pause} label={t("pos.action.hold", "Hold")} disabled={!billEditable || busy || !lines.length} onClick={() => { if (!guardEdit()) return; holdMutation.mutate(); }} />
              <ActionBtn color="bg-orange-500" icon={PercentIcon} label={t("pos.action.discount", "Discount")} disabled={!billEditable || busy || !lines.length} onClick={() => { if (!guardEdit()) return; setDiscountOpen(true); }} />
              <ActionBtn
                color="bg-rose-500"
                icon={Trash2}
                label={t("simple.posCancel", "Clear")}
                disabled={!billEditable || busy || !lines.length}
                onClick={() => {
                  if (!guardEdit()) return;
                  if (!window.confirm(t("simple.posClearConfirm", "Remove all items from this bill?"))) return;
                  lines.forEach((line) => lineMutation.mutate({ action: "void", lineId: line.id }));
                }}
              />
            </div>
            <button
              type="button"
              disabled={!billEditable || busy || !lines.length || paymentMutation.isPending}
              onClick={() => {
                if (!guardEdit()) return;
                setPayMethod("cash");
                setTendered(String(net || ""));
                setPayOpen(true);
              }}
              className="flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-violet-600 text-lg font-extrabold text-white hover:bg-violet-700 disabled:opacity-50"
              data-testid="simple-pos-settle"
            >
              {orderType === "DL"
                ? t("simple.posSettleDelivery", "Settle delivery")
                : t("simple.posSettle", "Settle / Pay")}
            </button>
          </div>
        </aside>
      </div>

      <PosSizePickerPopup
        open={Boolean(sizePickItem)}
        item={sizePickItem}
        pending={lineMutation.isPending}
        onPick={(size) => {
          const item = sizePickItem;
          setSizePickItem(null);
          if (item) addItem(item, size);
        }}
        onClose={() => setSizePickItem(null)}
      />

      {payOpen ? (
        <PaySheet
          net={net}
          method={payMethod}
          setMethod={setPayMethod}
          tendered={tendered}
          setTendered={setTendered}
          currencyCode={currencyCode}
          pending={paymentMutation.isPending}
          isDelivery={orderType === "DL"}
          bill={bill}
          branchId={branchId}
          onClose={() => setPayOpen(false)}
          onPay={({ method, tenderedAmount, deliveryPayload }) =>
            paymentMutation.mutate({ method, tenderedAmount, deliveryPayload })
          }
          t={t}
        />
      ) : null}

      {discountOpen ? (
        <div className="fixed inset-0 z-[200] grid place-items-center bg-black/45 p-4" onClick={() => setDiscountOpen(false)}>
          <div className="w-full max-w-sm rounded-2xl bg-white p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-extrabold">{t("pos.action.discount", "Discount")} %</h3>
            <Input type="number" min="0" max="100" value={discountPct} onChange={(e) => setDiscountPct(e.target.value)} className="mt-3 h-12" />
            <div className="mt-4 flex gap-2">
              <Button variant="outline" className="h-11 flex-1" onClick={() => setDiscountOpen(false)}>{t("common.cancel")}</Button>
              <Button className="h-11 flex-1 bg-orange-500 hover:bg-orange-600" disabled={discountMutation.isPending} onClick={() => discountMutation.mutate(discountPct)}>
                {t("common.save")}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {heldOpen ? (
        <HeldSheet
          heldBills={heldBills}
          onClose={() => setHeldOpen(false)}
          onRecall={(id) => recallMutation.mutate(id)}
          pending={recallMutation.isPending}
          t={t}
        />
      ) : null}

      <SimpleBillHistorySheet
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        branchId={branchId}
        openBusinessDate={openBusinessDate}
        reprintPending={reprintMutation.isPending}
        onReprint={(row) => {
          prepareBillPrintWindow();
          reprintMutation.mutate(row.id);
        }}
        onEdit={(row) => openHistoryBillMutation.mutate(row)}
        t={t}
      />
    </div>
  );
}

function PercentIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" className={props.className}>
      <line x1="19" y1="5" x2="5" y2="19" />
      <circle cx="6.5" cy="6.5" r="2.5" />
      <circle cx="17.5" cy="17.5" r="2.5" />
    </svg>
  );
}

function ActionBtn({ color, icon: Icon, label, onClick, disabled }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn("flex h-12 items-center justify-center gap-1.5 rounded-xl text-sm font-extrabold text-white disabled:opacity-50", color)}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}

function ProductTile({ item, disabled, onAdd }) {
  const [broken, setBroken] = useState(false);
  const img = resolveMediaUrl(item.img || item.image_url);
  const show = Boolean(img) && !broken;
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onAdd}
      className="simple-tile flex flex-col overflow-hidden bg-white text-left disabled:opacity-50"
      data-testid={`simple-pos-item-${item.id}`}
    >
      <div className="relative h-24 w-full bg-slate-100 sm:h-28">
        {show ? (
          <img src={img} alt="" className="absolute inset-0 h-full w-full object-cover" onError={() => setBroken(true)} />
        ) : (
          <div className="grid h-full place-items-center">
            <PosProductPlaceholder code={item.code} />
          </div>
        )}
        {itemHasSizes(item) ? (
          <span className="absolute bottom-1 end-1 rounded-md bg-black/60 px-1.5 py-0.5 text-[10px] font-bold text-white">S/M/L</span>
        ) : null}
      </div>
      <div className="space-y-0.5 p-2">
        <div className="line-clamp-2 text-[13px] font-extrabold leading-tight text-slate-900">{displayItemName(item)}</div>
        <div className="font-mono text-sm font-black text-violet-700">{aed(item.price)}</div>
      </div>
    </button>
  );
}

function normalizePhoneDigits(value) {
  return String(value || "").replace(/\D+/g, "");
}

function PaySheet({
  net,
  method,
  setMethod,
  tendered,
  setTendered,
  currencyCode,
  pending,
  onClose,
  onPay,
  t,
  isDelivery = false,
  bill = null,
  branchId = null,
}) {
  const phoneInputRef = React.useRef(null);
  const autoFilledForPhoneRef = React.useRef("");
  const [customerId, setCustomerId] = useState(bill?.customer_id || null);
  const [customerName, setCustomerName] = useState(
    bill?.customer_name && bill.customer_name !== "Walk-in" ? bill.customer_name : "",
  );
  const [phone, setPhone] = useState(bill?.delivery_contact || "");
  const [building, setBuilding] = useState(bill?.delivery_building_no || "");
  const [flat, setFlat] = useState(bill?.delivery_flat_no || "");
  const [landmark, setLandmark] = useState(bill?.delivery_address || "");
  const [areaId, setAreaId] = useState(bill?.delivery_area_id ? String(bill.delivery_area_id) : "");
  const [formError, setFormError] = useState("");
  const [showMatches, setShowMatches] = useState(false);
  const [debouncedPhone, setDebouncedPhone] = useState(() => normalizePhoneDigits(bill?.delivery_contact || ""));

  useEffect(() => {
    if (!isDelivery) return undefined;
    const timer = setTimeout(() => phoneInputRef.current?.focus(), 80);
    return () => clearTimeout(timer);
  }, [isDelivery]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedPhone(normalizePhoneDigits(phone)), 280);
    return () => clearTimeout(timer);
  }, [phone]);

  const searchReady = debouncedPhone.length >= 4;

  const { data: customerMatches = [], isFetching: customersFetching } = useQuery({
    queryKey: ["simple-pos-customers-phone", branchId, debouncedPhone],
    queryFn: async () => {
      const { data } = await api.get("/customers", {
        params: { branch_id: branchId, search: debouncedPhone },
      });
      return data.customers || [];
    },
    enabled: Boolean(isDelivery && branchId && searchReady),
  });

  const { data: savedLocations = [], isFetching: locationsFetching } = useQuery({
    queryKey: ["delivery-locations", branchId, debouncedPhone],
    queryFn: async () => {
      const { data } = await api.get("/delivery-locations", {
        params: { branch_id: branchId, contact: debouncedPhone },
      });
      return data.delivery_locations || [];
    },
    enabled: Boolean(isDelivery && branchId && searchReady),
  });

  const displayNet = net;
  const matchesLoading = customersFetching || locationsFetching;

  const phoneMatchedCustomers = useMemo(() => {
    if (!searchReady) return [];
    return customerMatches.filter((c) => {
      const digits = normalizePhoneDigits(c.mobile || c.phone || "");
      return digits.includes(debouncedPhone) || debouncedPhone.includes(digits);
    });
  }, [customerMatches, debouncedPhone, searchReady]);

  const applyCustomer = (customer) => {
    const nextPhone = customer.mobile || customer.phone || phone;
    setCustomerId(customer.id);
    setCustomerName(customer.name || "");
    setPhone(nextPhone || "");
    if (customer.address && !landmark.trim()) {
      setLandmark(customer.address);
    }
    autoFilledForPhoneRef.current = normalizePhoneDigits(nextPhone);
    setShowMatches(false);
    setFormError("");
  };

  const applyLocation = (loc, linkedCustomer = null) => {
    if (linkedCustomer) {
      setCustomerId(linkedCustomer.id);
      setCustomerName(linkedCustomer.name || loc.customer_name || "");
    } else {
      setCustomerName(loc.customer_name || customerName);
      if (!customerId) setCustomerId(null);
    }
    setPhone(loc.contact_no || phone);
    setBuilding(loc.building_no || "");
    setFlat(loc.flat_no || "");
    setLandmark(loc.address_line || "");
    setAreaId(loc.delivery_area_id ? String(loc.delivery_area_id) : "");
    autoFilledForPhoneRef.current = normalizePhoneDigits(loc.contact_no || phone);
    setShowMatches(false);
    setFormError("");
  };

  // Auto-fill once per phone when a single clear match appears.
  useEffect(() => {
    if (!isDelivery || !searchReady || matchesLoading) return;
    if (autoFilledForPhoneRef.current === debouncedPhone) return;
    if (savedLocations.length === 1 && phoneMatchedCustomers.length <= 1) {
      autoFilledForPhoneRef.current = debouncedPhone;
      applyLocation(savedLocations[0], phoneMatchedCustomers[0] || null);
      return;
    }
    if (savedLocations.length === 0 && phoneMatchedCustomers.length === 1) {
      autoFilledForPhoneRef.current = debouncedPhone;
      applyCustomer(phoneMatchedCustomers[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDelivery, searchReady, matchesLoading, debouncedPhone, savedLocations, phoneMatchedCustomers]);

  const onPhoneChange = (value) => {
    setPhone(value);
    setShowMatches(true);
    setCustomerId(null);
    autoFilledForPhoneRef.current = "";
  };

  const buildDeliveryPayload = () => {
    const payload = {
      customer_id: customerId || null,
      customer_name: customerName.trim() || "Walk-in",
      delivery_contact: phone.trim() || null,
      delivery_building_no: building.trim() || null,
      delivery_flat_no: flat.trim() || null,
      delivery_address: landmark.trim() || null,
    };
    // Keep area only when known from a saved address — not shown as a field.
    if (areaId) payload.delivery_area_id = Number(areaId);
    return payload;
  };

  const validateDelivery = () => {
    const hasAddress = landmark.trim() || (building.trim() && flat.trim());
    if (!phone.trim()) {
      setFormError(t("simple.posDeliveryPhoneRequired", "Phone number is required"));
      return false;
    }
    if (!hasAddress) {
      setFormError(t("simple.posDeliveryAddressRequired", "Enter building + flat, or a landmark"));
      return false;
    }
    setFormError("");
    return true;
  };

  const confirm = () => {
    if (isDelivery && !validateDelivery()) return;
    // Must open print window on the click gesture (Chrome blocks after async pay).
    prepareBillPrintWindow();
    onPay({
      method,
      tenderedAmount: Number(tendered) || displayNet,
      deliveryPayload: isDelivery ? buildDeliveryPayload() : undefined,
    });
  };

  const hasMatchPanel = isDelivery && showMatches && searchReady;

  return (
    <div className="fixed inset-0 z-[200] grid place-items-end bg-black/45 sm:place-items-center" onClick={onClose}>
      <div
        className="flex max-h-[92vh] w-full max-w-lg flex-col rounded-t-3xl bg-white sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="simple-pos-pay"
      >
        <div className="flex items-center justify-between border-b px-5 py-3">
          <div>
            <h3 className="text-xl font-extrabold">
              {isDelivery
                ? t("simple.posSettleDelivery", "Settle delivery")
                : t("simple.posSettle", "Settle / Pay")}
            </h3>
            {isDelivery ? (
              <p className="mt-0.5 text-[12px] text-slate-500">
                {t("simple.posDeliveryCollectHint", "Driver collects cash or card on delivery — pick which they will take")}
              </p>
            ) : null}
          </div>
          <button type="button" onClick={onClose} className="grid h-9 w-9 place-items-center rounded-lg bg-slate-100">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {isDelivery ? (
            <div className="space-y-3 rounded-2xl border border-emerald-100 bg-emerald-50/60 p-3" data-testid="simple-pos-delivery-form">
              <div className="flex items-center gap-2 text-sm font-extrabold text-emerald-900">
                <Truck className="h-4 w-4" />
                {t("simple.posDeliveryCustomer", "Customer & address")}
              </div>

              {/* Phone first — search & auto-fill */}
              <div className="space-y-1 relative">
                <Label className="text-emerald-900">
                  {t("simple.posPhone", "Phone")} *{" "}
                  <span className="font-normal text-emerald-700/80">
                    ({t("simple.posPhonePriority", "type to find customer")})
                  </span>
                </Label>
                <div className="relative">
                  <Phone className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-emerald-600" />
                  <Input
                    ref={phoneInputRef}
                    value={phone}
                    onChange={(e) => onPhoneChange(e.target.value)}
                    onFocus={() => setShowMatches(true)}
                    className="h-12 border-emerald-300 bg-white ps-9 text-base font-bold"
                    placeholder="05…"
                    inputMode="tel"
                    autoComplete="tel"
                    data-testid="simple-pos-delivery-phone"
                  />
                  {matchesLoading ? (
                    <Loader2 className="absolute end-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-emerald-600" />
                  ) : null}
                </div>

                {hasMatchPanel ? (
                  <div
                    className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded-xl border border-emerald-200 bg-white shadow-lg"
                    data-testid="simple-pos-customer-matches"
                  >
                    {phoneMatchedCustomers.length === 0 && savedLocations.length === 0 && !matchesLoading ? (
                      <div className="px-3 py-2 text-[12px] text-slate-500">
                        {t("simple.posNoCustomerMatch", "No saved customer — enter name & address below")}
                      </div>
                    ) : null}
                    {phoneMatchedCustomers.map((c) => (
                      <button
                        key={`cust-${c.id}`}
                        type="button"
                        onClick={() => applyCustomer(c)}
                        className="flex w-full items-start gap-2 border-b border-slate-100 px-3 py-2.5 text-start hover:bg-emerald-50"
                        data-testid={`simple-pos-pick-customer-${c.id}`}
                      >
                        <User className="mt-0.5 h-4 w-4 shrink-0 text-violet-600" />
                        <span className="min-w-0">
                          <span className="block text-sm font-bold text-slate-900">{c.name}</span>
                          <span className="block text-[11px] text-slate-500">
                            {c.mobile || c.phone || "—"}
                            {c.address ? ` · ${c.address}` : ""}
                          </span>
                        </span>
                      </button>
                    ))}
                    {savedLocations.map((loc) => (
                      <button
                        key={`loc-${loc.id}`}
                        type="button"
                        onClick={() => {
                          const linked = phoneMatchedCustomers.find((c) => {
                            const d = normalizePhoneDigits(c.mobile || c.phone || "");
                            return d && d === normalizePhoneDigits(loc.contact_no);
                          });
                          applyLocation(loc, linked || null);
                        }}
                        className="flex w-full items-start gap-2 border-b border-slate-100 px-3 py-2.5 text-start last:border-0 hover:bg-emerald-50"
                        data-testid={`simple-pos-pick-location-${loc.id}`}
                      >
                        <History className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                        <span className="min-w-0">
                          <span className="block text-sm font-bold text-slate-900">
                            {loc.customer_name || t("simple.posSavedAddress", "Saved address")}
                          </span>
                          <span className="block text-[11px] text-slate-500">
                            {loc.formatted_address || loc.address_line || "—"}
                            {loc.delivery_area_name ? ` · ${loc.delivery_area_name}` : ""}
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>

              <div className="space-y-1">
                <Label>{t("simple.posCustomerName", "Customer name")}</Label>
                <div className="relative">
                  <User className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    className="h-11 ps-9"
                    placeholder={t("simple.posCustomerName", "Customer name")}
                    data-testid="simple-pos-delivery-name"
                  />
                </div>
                {customerId ? (
                  <p className="text-[11px] font-semibold text-emerald-800">
                    {t("simple.posCustomerLinked", "Linked to customer #{{id}}", { id: customerId })}
                  </p>
                ) : null}
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label>{t("simple.posBuilding", "Building")}</Label>
                  <Input value={building} onChange={(e) => setBuilding(e.target.value)} className="h-11" data-testid="simple-pos-delivery-building" />
                </div>
                <div className="space-y-1">
                  <Label>{t("simple.posFlat", "Flat")}</Label>
                  <Input value={flat} onChange={(e) => setFlat(e.target.value)} className="h-11" data-testid="simple-pos-delivery-flat" />
                </div>
              </div>
              <div className="space-y-1">
                <Label>{t("simple.posLandmark", "Landmark / street")}</Label>
                <div className="relative">
                  <MapPin className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input value={landmark} onChange={(e) => setLandmark(e.target.value)} className="h-11 ps-9" data-testid="simple-pos-delivery-address" />
                </div>
              </div>
              {formError ? <p className="text-sm font-semibold text-red-600">{formError}</p> : null}
            </div>
          ) : null}

          <div className="text-center">
            <div className="text-sm text-slate-500">{t("simple.posTotal", "Total")}</div>
            <div className="text-3xl font-black text-violet-700">
              {aed(displayNet)}{" "}
              <span className="text-base font-bold text-slate-400">{currencyCode}</span>
            </div>
          </div>

          <div>
            <p className="mb-2 text-[12px] font-bold uppercase tracking-wide text-slate-500">
              {isDelivery
                ? t("simple.posDriverCollects", "Driver will collect")
                : t("simple.posPayMethod", "Payment method")}
            </p>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMethod("cash")}
                className={cn(
                  "flex min-h-[64px] flex-col items-center justify-center gap-1 rounded-xl px-2 py-2 text-center font-bold",
                  method === "cash" ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-700",
                )}
                data-testid="simple-pos-pay-cash"
              >
                <Banknote className="h-5 w-5" />
                <span className="text-sm leading-tight">
                  {isDelivery ? t("simple.posDriverCash", "Cash with driver") : t("pos.cash", "Cash")}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setMethod("card")}
                className={cn(
                  "flex min-h-[64px] flex-col items-center justify-center gap-1 rounded-xl px-2 py-2 text-center font-bold",
                  method === "card" ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-700",
                )}
                data-testid="simple-pos-pay-card"
              >
                <CreditCard className="h-5 w-5" />
                <span className="text-sm leading-tight">
                  {isDelivery ? t("simple.posDriverCard", "Card machine with driver") : t("pos.card", "Card")}
                </span>
              </button>
            </div>
          </div>

          {method === "cash" ? (
            <div>
              <Label>{t("pos.tendered", "Tendered")}</Label>
              <Input
                type="number"
                min="0"
                step="0.001"
                value={tendered}
                onChange={(e) => setTendered(e.target.value)}
                className="mt-1 h-12 text-lg font-bold"
              />
            </div>
          ) : null}
        </div>

        <div className="border-t px-5 py-3">
          <button
            type="button"
            disabled={pending}
            onClick={confirm}
            className="flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-violet-600 text-lg font-extrabold text-white disabled:opacity-50"
            data-testid="simple-pos-confirm-pay"
          >
            {pending ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
            {isDelivery
              ? t("simple.posConfirmDeliveryPay", "Save details & confirm")
              : t("simple.posConfirmPay", "Confirm payment")}
          </button>
        </div>
      </div>
    </div>
  );
}

function HeldSheet({ heldBills, onClose, onRecall, pending, t }) {
  return (
    <div className="fixed inset-0 z-[200] grid place-items-end bg-black/45 sm:place-items-center" onClick={onClose}>
      <div className="flex max-h-[80vh] w-full max-w-lg flex-col rounded-t-3xl bg-white sm:rounded-3xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="text-lg font-extrabold">{t("simple.posHeld", "Held")} ({heldBills.length})</h3>
          <button type="button" onClick={onClose} className="grid h-9 w-9 place-items-center rounded-lg bg-slate-100"><X className="h-4 w-4" /></button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-3">
          {heldBills.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-400">{t("simple.posNoHeld", "No held bills")}</div>
          ) : (
            <ul className="space-y-2">
              {heldBills.map((h) => (
                <li key={h.id}>
                  <button
                    type="button"
                    disabled={pending}
                    onClick={() => onRecall(h.id)}
                    className="flex w-full items-center justify-between rounded-xl border border-slate-100 bg-slate-50 px-3 py-3 text-start hover:bg-violet-50 disabled:opacity-50"
                  >
                    <div>
                      <div className="font-bold text-slate-900">{h.bill_number || h.sales_no}</div>
                      <div className="text-xs text-slate-500">{h.order_type_label || h.order_type} · {h.customer_name || "Walk-in"}</div>
                    </div>
                    <div className="font-mono font-bold text-violet-700">{aed(h.net_amount)}</div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
